/**
 * @file List & Loop (Multiple Responses) version of the additional documents
 * upload flow, per VADS recommendation to decouple document-type selection
 * from file input.
 *
 * Replaces the single-page `va-file-input-multiple` implementation
 * (evidenceChoiceAdditionalDocumentsPage.js) with an array-builder loop:
 *   1. Intro page - upload guidance
 *   2. Item page - single `va-file-input` + separate document-type select
 *   3. Summary page - uploaded-document cards + "add another" yes/no
 *
 * Shown when both the evidence enhancement AND FileInputV3 toggles are ON.
 * Data is stored in `additionalDocumentsLoop` and transformed into the
 * `additionalDocuments` submit shape by `transformListLoopDocuments` in
 * utils/submit.js.
 */
import {
  arrayBuilderItemFirstPageTitleUI,
  arrayBuilderYesNoUI,
  arrayBuilderYesNoSchema,
  fileInputUI,
  fileInputSchema,
  selectUI,
  selectSchema,
} from 'platform/forms-system/src/js/web-component-patterns';
import { arrayBuilderPages } from 'platform/forms-system/src/js/patterns/array-builder';
import { MISSING_FILE } from 'platform/forms-system/src/js/validation';
import { hasOtherEvidence, isEvidenceEnhancement } from '../../utils';
import { standardTitle } from '../../content/form0781';
import {
  evidenceChoiceAdditionalDocumentsTitle,
  evidenceChoiceAdditionalDocumentsContent,
} from '../../../shared/content/evidenceChoiceAdditionalDocumentsPage';
import {
  FILE_TYPES,
  HINT_TEXT,
  UPLOAD_URL,
  ATTACHMENTS_TYPE,
  ADDITIONAL_ATTACHMENT_LABEL,
} from '../../../shared/components/fileInputComponent/constants';
import {
  createPayload,
  parseResponse,
} from '../../../shared/utils/fileInputMultiUIConfig';

/** Value-to-label map for the document-type select and summary cards. */
export const ATTACHMENT_LABELS = ATTACHMENTS_TYPE.reduce(
  (acc, { value, label }) => ({ ...acc, [value]: label }),
  {},
);

/** Gate shared by every page in the loop: evidence enhancement + FileInputV3 + user said Yes. */
export const showAdditionalDocumentsListLoop = formData =>
  hasOtherEvidence(formData) &&
  isEvidenceEnhancement(formData) &&
  formData.disability526SupportingEvidenceFileInputV3;

/** @type {ArrayBuilderOptions} */
export const arrayBuilderOptions = {
  arrayPath: 'additionalDocumentsLoop',
  nounSingular: 'supporting document',
  nounPlural: 'supporting documents',
  required: showAdditionalDocumentsListLoop,
  isItemIncomplete: item =>
    !item?.uploadedDocument?.confirmationCode || !item?.attachmentId,
  text: {
    getItemName: item => item?.uploadedDocument?.name || 'Supporting document',
    cardDescription: item => ATTACHMENT_LABELS[item?.attachmentId] || '',
    cancelAddYes: 'Yes, cancel adding this document',
    cancelAddNo: 'No, continue adding this document',
    deleteTitle: 'Delete this document',
    deleteYes: 'Yes, delete this document',
    deleteNo: 'No, keep this document',
  },
};

/** @returns {PageSchema} */
const introPage = {
  uiSchema: {
    'ui:title': standardTitle(evidenceChoiceAdditionalDocumentsTitle),
    'ui:description': evidenceChoiceAdditionalDocumentsContent,
  },
  schema: {
    type: 'object',
    properties: {},
  },
};

/** @returns {PageSchema} */
const summaryPage = {
  uiSchema: {
    'view:hasAnotherSupportingDocument': arrayBuilderYesNoUI(
      arrayBuilderOptions,
      {
        title: 'Do you have a supporting document to upload?',
        labels: {
          Y: 'Yes, I have a document to upload',
          N: 'No, I don’t have a document to upload',
        },
      },
      {
        title: 'Do you have another supporting document to upload?',
        labels: {
          Y: 'Yes, I have another document to upload',
          N: 'No, I don’t have another document to upload',
        },
      },
    ),
  },
  schema: {
    type: 'object',
    properties: {
      'view:hasAnotherSupportingDocument': arrayBuilderYesNoSchema,
    },
    required: ['view:hasAnotherSupportingDocument'],
  },
};

/**
 * Item page: single file upload plus a document-type select rendered as a
 * separate form control (decoupled from the file card, per the VADS
 * recommendation).
 * @returns {PageSchema}
 */
const uploadPage = {
  uiSchema: {
    ...arrayBuilderItemFirstPageTitleUI({
      title: 'Upload supporting document',
      nounSingular: arrayBuilderOptions.nounSingular,
      hasMultipleItemPages: false,
    }),
    uploadedDocument: {
      ...fileInputUI({
        title: 'Select a file to upload',
        hint: HINT_TEXT,
        required: () => true,
        accept: FILE_TYPES,
        fileUploadUrl: UPLOAD_URL,
        formNumber: '21-526EZ',
        fileSizesByFileType: {
          pdf: {
            maxFileSize: 1024 * 1024 * 100,
            minFileSize: 1024,
          },
          default: {
            maxFileSize: 1024 * 1024 * 50,
            minFileSize: 1,
          },
        },
        errorMessages: { required: MISSING_FILE },
        createPayload,
        parseResponse,
      }),
      'ui:validations': [
        // Needed for validation to work correctly inside arrays; taken from
        // the ownedAssetPages.js precedent.
        // https://va.ghe.com/software/vets-design-system-documentation/issues/4837
        (errors, fieldData) => {
          if (fieldData?.isEncrypted && !fieldData?.confirmationCode) {
            return;
          }
          if (!fieldData || !fieldData.name) {
            errors.addError(MISSING_FILE);
          }
          if (fieldData?.errorMessage) {
            errors.addError(fieldData.errorMessage);
          }
        },
      ],
    },
    attachmentId: selectUI({
      title: ADDITIONAL_ATTACHMENT_LABEL,
      required: () => true,
      labels: ATTACHMENT_LABELS,
      errorMessages: { required: 'Choose a document type' },
    }),
  },
  schema: {
    type: 'object',
    required: ['uploadedDocument', 'attachmentId'],
    properties: {
      uploadedDocument: fileInputSchema(),
      attachmentId: selectSchema(Object.keys(ATTACHMENT_LABELS)),
    },
  },
};

/**
 * Array-builder page set for the additional documents List & Loop flow.
 * Spread into the supportingEvidence chapter's `pages` object.
 */
export const additionalDocumentsListLoopPages = arrayBuilderPages(
  arrayBuilderOptions,
  pageBuilder => ({
    evidenceChoiceDocumentsLoopIntro: pageBuilder.introPage({
      title: evidenceChoiceAdditionalDocumentsTitle,
      path: 'supporting-evidence/additional-evidence-uploads-intro',
      depends: showAdditionalDocumentsListLoop,
      uiSchema: introPage.uiSchema,
      schema: introPage.schema,
    }),
    evidenceChoiceDocumentsLoopSummary: pageBuilder.summaryPage({
      title: 'Review your supporting documents',
      path: 'supporting-evidence/additional-evidence-uploads-summary',
      depends: showAdditionalDocumentsListLoop,
      uiSchema: summaryPage.uiSchema,
      schema: summaryPage.schema,
    }),
    evidenceChoiceDocumentsLoopUpload: pageBuilder.itemPage({
      title: 'Upload supporting document',
      path: 'supporting-evidence/additional-evidence-uploads/:index/document',
      depends: showAdditionalDocumentsListLoop,
      uiSchema: uploadPage.uiSchema,
      schema: uploadPage.schema,
    }),
  }),
);
